export type HumanReadableResource = {
  id: string;
  name?: string;
  type?: string;
  resourceGroup?: string;
  location?: string;
  tags?: Record<string, string>;
};

export function normalizeRoleDefinitionId(id?: string): string {
  if (!id) return "";
  const match = id.match(/roleDefinitions\/([0-9a-fA-F-]{36})$/);
  if (match) return match[1];
  // If already a GUID, return as-is
  if (/^[0-9a-fA-F-]{36}$/.test(id)) return id;
  return id;
}
export function parseResourceFromScope(
  scope: string
): HumanReadableResource | undefined {
  // Scope examples:
  // /subscriptions/{subId}
  // /subscriptions/{subId}/resourceGroups/{rg}
  // /subscriptions/{subId}/resourceGroups/{rg}/providers/{typeNamespace}/{typePath}/{resourceName}
  if (!scope) return undefined;
  const parts = scope.split("/").filter(Boolean);
  if (parts.length < 2) return undefined;
  const idxRG = parts.indexOf("resourceGroups");
  if (idxRG === -1) return undefined;
  const resourceGroup = parts[idxRG + 1] ?? undefined;
  const idxProviders = parts.indexOf("providers");
  if (idxProviders === -1) {
    return {
      id: scope,
      resourceGroup,
    };
  }
  const providerNs = parts[idxProviders + 1] ?? null;
  const typeAndName = parts.slice(idxProviders + 2);
  // type path can be multiple segments, last segment is name
  if (typeAndName.length < 2) {
    return {
      id: scope,
      type: providerNs,
      resourceGroup,
    };
  }
  const name = typeAndName[typeAndName.length - 1] ?? null;
  const type = [providerNs, ...typeAndName.slice(0, -1)].join("/");
  return {
    id: scope,
    name,
    type,
    resourceGroup,
  };
}

/** Matches a well-formed Azure Function App (Web site) resource ID, e.g.
 * /subscriptions/<id>/resourceGroups/<rg>/providers/Microsoft.Web/sites/<name>,
 */
const FUNCTION_APP_RESOURCE_ID_PATTERN =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.Web\/sites\/([^/]+)$/i;

export function isAzureFunctionAppResourceId(
  resourceId: string | undefined
): boolean {
  return FUNCTION_APP_RESOURCE_ID_PATTERN.test(resourceId ?? "");
}

/** Extracts the Function App (Web site) name from its resource ID, matching
 * case-insensitively since Azure resource IDs compare case-insensitively.
 * Returns `undefined` if the resource ID is not a valid Function App resource
 * ID.
 */
export function functionAppNameFromResourceId(
  resourceId: string | undefined
): string | undefined {
  return resourceId?.match(FUNCTION_APP_RESOURCE_ID_PATTERN)?.[1];
}

/** Matches a well-formed Azure virtual machine resource ID, e.g.
 * /subscriptions/<id>/resourceGroups/<rg>/providers/Microsoft.Compute/virtualMachines/<name>,
 */
const VIRTUAL_MACHINE_RESOURCE_ID_PATTERN =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.Compute\/virtualMachines\/([^/]+)$/i;

export function isAzureVirtualMachineResourceId(
  resourceId: string | undefined
): boolean {
  return VIRTUAL_MACHINE_RESOURCE_ID_PATTERN.test(resourceId ?? "");
}

/** Extracts the virtual machine name from its resource ID, matching
 * case-insensitively since Azure resource IDs compare case-insensitively.
 * Returns `undefined` if the resource ID is not a valid virtual machine
 * resource ID.
 */
export function virtualMachineNameFor(
  resourceId: string | undefined
): string | undefined {
  return resourceId?.match(VIRTUAL_MACHINE_RESOURCE_ID_PATTERN)?.[1];
}

/** Matches a well-formed Azure role definition resource ID, at any scope from
 * which it can be referenced:
 * - subscription-scoped, e.g.
 *   /subscriptions/<id>/providers/Microsoft.Authorization/roleDefinitions/<guid>
 * - management-group-scoped, e.g.
 *   /providers/Microsoft.Management/managementGroups/<id>/providers/Microsoft.Authorization/roleDefinitions/<guid>
 * - unscoped, e.g. /providers/Microsoft.Authorization/roleDefinitions/<guid> —
 *   built-in roles, and custom roles referenced from their defining scope's
 *   root (Azure returns a role definition's `id` relative to the scope it was
 *   queried from, so a management-group-scoped custom role can also appear in
 *   this shorter form).
 */
const ROLE_DEFINITION_RESOURCE_ID_PATTERN =
  /^(?:\/subscriptions\/[^/]+|\/providers\/Microsoft\.Management\/managementGroups\/[^/]+)?\/providers\/Microsoft\.Authorization\/roleDefinitions\/([^/]+)$/i;

export function isAzureRoleDefinitionResourceId(
  resourceId: string | undefined
): boolean {
  return ROLE_DEFINITION_RESOURCE_ID_PATTERN.test(resourceId ?? "");
}

/** Derives the public Azure Function App base URL from its resource ID.
 *
 * The site name is the last segment of the resource ID and maps to
 * `https://<name>.azurewebsites.net`. Returns `undefined` if the resource ID is
 * not a valid Function App resource ID.
 */
export function functionAppUrlFromResourceId(
  resourceId: string | undefined
): string | undefined {
  const name = functionAppNameFromResourceId(resourceId);
  return name ? `https://${name}.azurewebsites.net` : undefined;
}
